import { useMemo } from 'react';

import { t } from '@lingui/macro';

import useSentinelTeam from 'hooks/useSentinelTeam';

/**
 * Fault severity labels, keyed by the uppercase severity used throughout the
 * app (and by the FaultSeverityEnum on filter inputs).
 */
export type FaultSeverityLabelMap = Record<FaultSeverityEnum, string>;

/**
 * The application's own wording for the four severities — the single source of
 * these strings in the UI.
 *
 * This is a function, not a constant, because `t` must be evaluated at render
 * time: a module-scope `` t`Severe` `` is frozen to whichever locale was active
 * when the module first loaded and never updates on a locale change.
 */
export const defaultFaultSeverityLabels = (): FaultSeverityLabelMap => ({
  SEVERE: t`Severe`,
  ATTENTION: t`Attention`,
  MODERATE: t`Moderate`,
  UNKNOWN: t`Unknown`,
});

/**
 * Resolve a label from a raw severity value.
 *
 * Faults arrive from the API lowercase (`severity: "severe"`), while the
 * FaultSeverityEnum used for filters and alert configuration is uppercase
 * (`SEVERE`). This normalizes both, and falls back to the Unknown label for a
 * missing or unrecognized severity.
 */
export const labelForSeverity = (
  severity: string | null | undefined,
  labels: FaultSeverityLabelMap,
): string =>
  labels[severity?.toUpperCase() as FaultSeverityEnum] ?? labels.UNKNOWN;

/** Shape shared by FaultSeverityLabels and FaultSeverityLabelOverrides. */
type PartialLabels = {
  severe?: string | null;
  attention?: string | null;
  moderate?: string | null;
  unknown?: string | null;
};

const withFallback = (labels?: PartialLabels): FaultSeverityLabelMap => {
  const defaults = defaultFaultSeverityLabels();

  return {
    SEVERE: labels?.severe || defaults.SEVERE,
    ATTENTION: labels?.attention || defaults.ATTENTION,
    MODERATE: labels?.moderate || defaults.MODERATE,
    UNKNOWN: labels?.unknown || defaults.UNKNOWN,
  };
};

/**
 * The fault severity labels to display for the current team, already resolved
 * server-side through team override -> OEM segment preset -> application default.
 *
 * This is the single source of fault severity wording in the UI. Do not read
 * `SEVERITY_TEXT` for the four fault severities, and do not derive a label by
 * capitalizing the raw value.
 */
export default function useFaultSeverityLabels(): FaultSeverityLabelMap {
  const { faultSeverityLabels } = useSentinelTeam();

  return useMemo(
    () => withFallback(faultSeverityLabels),
    [faultSeverityLabels],
  );
}

/**
 * The labels this team would show with no overrides of its own — its OEM segment
 * preset, else the application default.
 *
 * Used by Admin > Preferences to preview what a cleared custom label falls back
 * to. Distinct from `useFaultSeverityLabels`, which includes the team's own
 * overrides and so cannot answer "what happens if I clear this?".
 */
export function useFaultSeverityLabelDefaults(): FaultSeverityLabelMap {
  const { faultSeverityLabelDefaults } = useSentinelTeam();

  return useMemo(
    () => withFallback(faultSeverityLabelDefaults),
    [faultSeverityLabelDefaults],
  );
}
